import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '../hooks/useMediaQuery';
import Loader from '../Components/Loader';

const countryCodes = {
  'Afghanistan': '+93', 'Albania': '+355', 'Algeria': '+213', 'Argentina': '+54', 
  'Australia': '+61', 'Austria': '+43', 'Bangladesh': '+880', 'Belgium': '+32', 
  'Brazil': '+55', 'Canada': '+1', 'Chile': '+56', 'China': '+86', 'Colombia': '+57',
  'Czech Republic': '+420', 'Denmark': '+45', 'Egypt': '+20', 'Finland': '+358', 
  'France': '+33', 'Germany': '+49', 'Ghana': '+233', 'Greece': '+30', 'Hungary': '+36',
  'India': '+91', 'Indonesia': '+62', 'Iran': '+98', 'Iraq': '+964', 'Ireland': '+353',
  'Israel': '+972', 'Italy': '+39', 'Japan': '+81', 'Kenya': '+254', 'Malaysia': '+60',
  'Mexico': '+52', 'Morocco': '+212', 'Netherlands': '+31', 'New Zealand': '+64',
  'Nigeria': '+234', 'Norway': '+47', 'Pakistan': '+92', 'Philippines': '+63',
  'Poland': '+48', 'Portugal': '+351', 'Romania': '+40', 'Russia': '+7',
  'Saudi Arabia': '+966', 'South Africa': '+27', 'South Korea': '+82', 'Spain': '+34',
  'Sweden': '+46', 'Switzerland': '+41', 'Thailand': '+66', 'Turkey': '+90',
  'Ukraine': '+380', 'United Arab Emirates': '+971', 'United Kingdom': '+44',
  'United States': '+1', 'Vietnam': '+84', 'Zimbabwe': '+263'
};

const countries = Object.keys(countryCodes).sort();

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? (window.innerWidth < 768 ? 100 : 300) : (window.innerWidth < 768 ? -100 : -300),
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? (window.innerWidth < 768 ? 100 : 300) : (window.innerWidth < 768 ? -100 : -300),
    opacity: 0
  })
};

export default function PersonalInfo() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [[step, direction], setStep] = useState([1, 0]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    age: '',
    dob: '',
    country: '',
    phone: ''
  });

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (formData.country && countryCodes[formData.country]) {
      const code = countryCodes[formData.country];
      if (!formData.phone || formData.phone.match(/^\+/)) {
        setFormData(prev => ({ ...prev, phone: code + ' ' }));
      }
    }
  }, [formData.country]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login');
      return;
    }
    setUser(user);
    
    // Check if user already has personal data (resuming onboarding)
    const { data: personal } = await supabase
      .from('personal')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
      
    if (personal && personal.name) {
      // Pre-fill existing data
      setFormData({
        fullName: personal.name || '',
        age: personal.age?.toString() || '',
        dob: personal.dob || '',
        country: personal.country || '',
        phone: personal.phone || ''
      });
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleDobChange = (e) => {
    const dob = e.target.value;
    const age = calculateAge(dob);
    setFormData({ ...formData, dob, age: age.toString() });
  };

  const paginate = (newDirection) => {
    if (newDirection > 0) {
      if (step === 1 && !formData.fullName.trim()) {
        setMessage('Please enter your full name');
        return;
      }
      if (step === 2 && !formData.dob) {
        setMessage('Please enter your date of birth');
        return;
      }
      if (step === 2) {
        const age = parseInt(formData.age);
        if (!age || age < 13) {
          setMessage('You must be at least 13 years old');
          return;
        }
      }
      if (step === 3 && !formData.country) {
        setMessage('Please select your country');
        return;
      }
      if (step === 4 && !formData.phone.trim()) {
        setMessage('Please enter your phone number');
        return;
      }
    }
    
    setMessage('');
    setStep([step + newDirection, newDirection]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.phone.trim()) {
      setMessage('Please enter your phone number');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const { error } = await supabase
        .from('personal')
        .upsert({
          user_id: user.id,
          name: formData.fullName,
          age: parseInt(formData.age),
          dob: formData.dob,
          country: formData.country,
          phone: formData.phone,
          status: 'active'  // <-- COMPLETING ONBOARDING
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Insert error:', error);
        setMessage('Error saving: ' + error.message);
        setLoading(false);
        return;
      }

      setMessage('✓ Profile saved! Redirecting...');
      setTimeout(() => navigate('/home'), 2000);
    } catch (err) {
      console.error('Save error:', err);
      setMessage('Something went wrong: ' + err.message);
      setLoading(false);
    }
  };

  if (!user) return <Loader size={isMobile ? 80 : 120} />;

  if (loading) {
    return (
      <div style={getContainerStyle(isMobile)}>
        <div style={loaderContainerStyle}>
          <Loader size={isMobile ? 100 : 150} />
          <p style={loaderTextStyle}>Saving your profile...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div style={getStepContainerStyle(isMobile)}>
            <h2 style={getStepTitleStyle(isMobile)}>What's your full name?</h2>
            <p style={getStepSubtitleStyle(isMobile)}>Step 1 of 4</p>
            <input
              type="text"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={e => setFormData({...formData, fullName: e.target.value})}
              style={getInputStyle(isMobile)}
              autoFocus
            />
          </div>
        );
      case 2:
        return (
          <div style={getStepContainerStyle(isMobile)}>
            <h2 style={getStepTitleStyle(isMobile)}>When were you born?</h2>
            <p style={getStepSubtitleStyle(isMobile)}>Step 2 of 4</p>
            <input
              type="date"
              value={formData.dob}
              onChange={handleDobChange}
              max={new Date().toISOString().split('T')[0]}
              style={getInputStyle(isMobile)}
            />
            {formData.age && (
              <p style={ageDisplayStyle}>You are {formData.age} years old</p>
            )}
          </div>
        );
      case 3:
        return (
          <div style={getStepContainerStyle(isMobile)}>
            <h2 style={getStepTitleStyle(isMobile)}>Where are you from?</h2>
            <p style={getStepSubtitleStyle(isMobile)}>Step 3 of 4</p>
            <select
              value={formData.country}
              onChange={e => setFormData({...formData, country: e.target.value})}
              style={getInputStyle(isMobile)}
            >
              <option value="">Select your country</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        );
      case 4:
        return (
          <div style={getStepContainerStyle(isMobile)}>
            <h2 style={getStepTitleStyle(isMobile)}>What's your phone number?</h2>
            <p style={getStepSubtitleStyle(isMobile)}>Step 4 of 4</p>
            <div style={getPhoneContainerStyle(isMobile)}>
              <span style={getCountryCodeStyle(isMobile)}>
                {formData.country ? countryCodes[formData.country] || '+' : '+'}
              </span>
              <input
                type="tel"
                placeholder="Phone number"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                style={getPhoneInputStyle(isMobile)}
              />
            </div>
            <p style={getHintStyle(isMobile)}>We'll use this for account recovery</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={getContainerStyle(isMobile)}>
      <div style={getContentStyle(isMobile)}>
        {/* Progress Bar */}
        <div style={getProgressContainerStyle(isMobile)}>
          {[1, 2, 3, 4].map((num) => (
            <div key={num} style={getProgressDotStyle(num <= step, isMobile)} />
          ))}
        </div>

        {/* Animated Step Content */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            style={motionContainerStyle}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div style={getButtonContainerStyle(isMobile)}>
          {step > 1 && (
            <button 
              onClick={() => paginate(-1)}
              style={getBackButtonStyle(isMobile)}
              type="button"
            >
              ← Back
            </button>
          )}
          
          {step < 4 ? (
            <button 
              onClick={() => paginate(1)}
              style={getNextButtonStyle(isMobile)}
              type="button"
            >
              Next →
            </button>
          ) : (
            <button 
              onClick={handleSave}
              style={getSaveButtonStyle(isMobile)}
              type="submit"
            >
              Save & Continue →
            </button>
          )}
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={message.includes('✓') ? successMessageStyle : errorMessageStyle}
            >
              {message}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Styles remain the same...
const getContainerStyle = (isMobile) => ({
  minHeight: '100vh',
  background: 'var(--bg, #1a1b1e)',
  display: 'flex',
  flexDirection: 'column',
  padding: isMobile ? '16px' : '20px',
  color: 'var(--text, #fff)',
});

const getContentStyle = (isMobile) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: '450px',
  width: '100%',
  margin: '0 auto',
  paddingTop: isMobile ? '40px' : '0',
});

const getProgressContainerStyle = (isMobile) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: isMobile ? '8px' : '12px',
  marginBottom: isMobile ? '30px' : '40px',
});

const getProgressDotStyle = (active, isMobile) => ({
  width: isMobile ? '10px' : '12px',
  height: isMobile ? '10px' : '12px',
  borderRadius: '50%',
  background: active ? '#38bdf8' : '#2d2d2d',
  border: `2px solid ${active ? '#38bdf8' : '#404040'}`,
  transition: 'all 0.3s ease',
});

const motionContainerStyle = {
  width: '100%',
};

const getStepContainerStyle = (isMobile) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: isMobile ? '12px' : '16px',
  minHeight: isMobile ? '180px' : '200px',
});

const getStepTitleStyle = (isMobile) => ({
  fontSize: isMobile ? '24px' : '28px',
  fontWeight: 'bold',
  margin: '0 0 8px 0',
  textAlign: 'center',
  color: '#fff',
  lineHeight: '1.3',
});

const getStepSubtitleStyle = (isMobile) => ({
  fontSize: isMobile ? '13px' : '14px',
  color: '#64748b',
  textAlign: 'center',
  margin: '0 0 20px 0',
});

const getInputStyle = (isMobile) => ({
  padding: isMobile ? '14px' : '16px',
  borderRadius: '12px',
  border: '1px solid var(--border, #2d2d2d)',
  background: 'var(--card-bg, #1e1f22)',
  color: '#fff',
  fontSize: '16px',
  outline: 'none',
  width: '100%',
  minHeight: '48px',
  boxSizing: 'border-box',
});

const ageDisplayStyle = {
  textAlign: 'center',
  color: '#38bdf8',
  fontSize: '14px',
  margin: '8px 0 0 0',
};

const getPhoneContainerStyle = (isMobile) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '8px' : '12px',
});

const getCountryCodeStyle = (isMobile) => ({
  padding: isMobile ? '14px' : '16px',
  borderRadius: '12px',
  background: 'var(--card-bg, #1e1f22)',
  color: '#38bdf8',
  fontWeight: 'bold',
  fontSize: '16px',
  border: '1px solid var(--border, #2d2d2d)',
  minWidth: isMobile ? '50px' : '60px',
  textAlign: 'center',
});

const getPhoneInputStyle = (isMobile) => ({
  flex: 1,
  padding: isMobile ? '14px' : '16px',
  borderRadius: '12px',
  border: '1px solid var(--border, #2d2d2d)',
  background: 'var(--card-bg, #1e1f22)',
  color: '#fff',
  fontSize: '16px',
  outline: 'none',
  minHeight: '48px',
});

const getHintStyle = (isMobile) => ({
  fontSize: isMobile ? '11px' : '12px',
  color: '#64748b',
  textAlign: 'center',
  margin: '8px 0 0 0',
});

const getButtonContainerStyle = (isMobile) => ({
  display: 'flex',
  gap: isMobile ? '10px' : '12px',
  marginTop: isMobile ? '24px' : '32px',
  justifyContent: 'center',
  flexWrap: 'wrap',
});

const getBackButtonStyle = (isMobile) => ({
  padding: isMobile ? '12px 20px' : '14px 24px',
  borderRadius: '12px',
  border: '1px solid var(--border, #2d2d2d)',
  background: 'transparent',
  color: '#64748b',
  fontSize: isMobile ? '15px' : '16px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s',
  minHeight: '48px',
  flex: isMobile ? '1' : 'none',
});

const getNextButtonStyle = (isMobile) => ({
  padding: isMobile ? '14px 24px' : '14px 32px',
  borderRadius: '12px',
  border: 'none',
  background: '#38bdf8',
  color: '#000',
  fontSize: isMobile ? '16px' : '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s',
  flex: 1,
  minHeight: '52px',
});

const getSaveButtonStyle = (isMobile) => ({
  padding: isMobile ? '14px 24px' : '14px 32px',
  borderRadius: '12px',
  border: 'none',
  background: '#22c55e',
  color: '#fff',
  fontSize: isMobile ? '16px' : '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s',
  flex: 1,
  minHeight: '52px',
});

const errorMessageStyle = {
  marginTop: '20px',
  padding: '14px 16px',
  borderRadius: '12px',
  background: 'rgba(239, 68, 68, 0.1)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  color: '#ef4444',
  fontSize: '14px',
  textAlign: 'center',
  lineHeight: '1.5',
};

const successMessageStyle = {
  marginTop: '20px',
  padding: '14px 16px',
  borderRadius: '12px',
  background: 'rgba(34, 197, 94, 0.1)',
  border: '1px solid rgba(34, 197, 94, 0.3)',
  color: '#22c55e',
  fontSize: '14px',
  textAlign: 'center',
  lineHeight: '1.5',
};

const loaderContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '24px',
};

const loaderTextStyle = {
  color: '#64748b',
  fontSize: '16px',
  fontWeight: '500',
};